import {
  Button,
  TextField,
  Checkbox,
  AppHeader,
  Typography,
  Alert,
  Form,
  AddressSearch,
  Box,
  useAddressSearchValidator,
  Select,
  MenuItem,
  Grid,
} from '@ace-clublabs/shared-component-library';
import React, { useContext, useEffect, useState, useLayoutEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { NewMembershipFormData } from '../application/models/NewJoinSessionData';
import { Address } from '@lib/types';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { getNextPage } from '../utils/getNextPage';
import { UtagType } from '../utils/UtagHelper';
import { FlowType, MembershipLevel, CurrentPayPlan } from '@ace-clublabs/common-membership-api-types';
import { EmployeeInvalidClubModal } from '../components/EmployeeInvalidClubModal';
import { getCostByMembershipLevel, getProgressProps } from '../utils/helpers';
import { AppProgressHeader } from '../components/AppProgressHeader';
import { getAddressPageTitle } from '../utils/stringHelper';
import { sources } from '../api/sources';
import { MembershipUpdateModal } from '../components/MembershipUpdateModal';
import { numberHelper } from '../utils/numberHelper';
import { ConfirmZipCodeModal } from '../components/ConfirmZipCodeModal';

interface IAutoCompleteAddressPageProps {
  addressType: 'residence' | 'mailing';
}

export const AutoCompleteAddressPage: React.FC<IAutoCompleteAddressPageProps> = ({ addressType }) => {
  const {
    sessionData,
    submitMembershipForm,
    updateCache,
    loadingProvider,
    loadingInfo,
    membershipInfo,
    refreshSession,
    sendTag,
    updateMembershipForm,
    updateMembershipSession,
    getNewBusinessCost,
    reinitializeSessionForZipChange,
  } = useContext(MembershipAppStateContext);

  const { formData, sessionId } = sessionData;
  const history = useHistory();
  const [isTagging, setIsTagging] = useState(true);
  const [serverValidationErrors, setServerValidationErrors] = useState({ hasError: false, error: '' });
  const [showAnnualOnlyModal, setShowAnnualOnlyModal] = useState(false);
  const [showClubMismatchModal, setShowClubMismatchModal] = useState(false);
  const [showOotModal, setShowOotModal] = useState(false);
  const [clubMismatchPricing, setClubMismatchPricing] = useState<{
    primaryMemberCost: number;
    billPlan: string;
  } | null>(null);

  const addressExist =
    addressType === 'residence'
      ? formData.residenceAddress && formData.residenceAddress.streetAddress !== ''
      : formData.mailingAddress && formData.mailingAddress.streetAddress !== '';

  const [addressSelected, setAddressSelected] = useState(addressExist);
  const addressValidator = useAddressSearchValidator({ required: !addressSelected, requireCounty: false });
  const buildAddressSearchValue = (address: Address | undefined) => {
    if (!address?.streetAddress || !address?.city || !address?.state || !address?.zipCode) {
      return null;
    }

    const secondaryText = `${address.city}, ${address.state} ${address.zipCode}`.trim();
    return {
      id: address.googleAddressId ?? '',
      shortDisplay: {
        primaryText: address.streetAddress,
        secondaryText,
      },
      longDisplay: {
        primaryText: address.streetAddress,
        secondaryText,
      },
      shortParts: {
        street: address.streetAddress,
        city: address.city,
        state: address.state,
        zip: address.zipCode,
      },
      longParts: {
        street: address.streetAddress,
        city: address.city,
        state: address.state,
        zip: address.zipCode,
      },
    };
  };
  const isGettingResidenceAddress = addressType === 'residence';
  const currentPage = addressType === 'residence' ? Pages.ResidenceAddressPage : Pages.MailingAddressPage;
  const isGiftFlow = sessionData.flowType === FlowType.GIFT;
  const isGrouponFlow = sessionData.flowType === FlowType.GROUPON;
  const isMajorRocketFlow = sessionData.flowType === FlowType.MAJOR_ROCKET;
  const isEmployeeFlow = sessionData.flowType === FlowType.EMPLOYEE;
  const isTMobileFlow = sessionData.flowType === FlowType.T_MOBILE;
  const pageNameTag = `${isGiftFlow ? 'membership:gift:recipient address' : 'contact information'}`;
  const allowOneTime = membershipInfo?.allowedBillPlans.includes(CurrentPayPlan.ONETIME);

  const differentAddressText = isGiftFlow
    ? 'Recipient has a different mailing address'
    : 'I have a different mailing address';
  // set and modified when mailing address checkbox is clicked so new form renders without internal state
  const [formKey, setFormKey] = useState(new Date().toISOString());
  const [showEmployeeInvalidClubModal, setEmployeeInvalidClubModal] = useState(false);

  const getInitialResidenceAddressState = (address: Address | undefined): Address => {
    return {
      streetAddress: address?.streetAddress || '',
      unitType: address?.unitType || '',
      unitNumber: address?.unitNumber || '',
      city: address?.city || '',
      state: address?.state || '',
      zipCode: address?.zipCode?.slice(0, 5) || '',
      googleAddressId: address?.googleAddressId || '',
    };
  };

  const getInitialMailingAddressState = (address: Address | undefined): Address => {
    return {
      streetAddress: address?.streetAddress || '',
      city: address?.city || '',
      state: address?.state || '',
      zipCode: address?.zipCode?.slice(0, 5) || '',
    };
  };

  const tag = () => {
    sendTag(UtagType.view, pageNameTag);
    setIsTagging(false);
  };

  const [residenceAddressForm, setResidenceAddressFormData] = useState(
    getInitialResidenceAddressState(formData.residenceAddress)
  );

  const [residenceAddressSearchValue, setResidenceAddressSearchValue] = useState(() =>
    buildAddressSearchValue(formData.residenceAddress)
  );
  const [hasMailingAddress, setHasMailingAddress] = useState(
    formData.mailingAddress != undefined &&
      (formData.mailingAddress.streetAddress !== '' ||
        formData.mailingAddress?.city !== '' ||
        formData.mailingAddress?.state !== '' ||
        formData.mailingAddress?.zipCode !== '')
  );

  const [mailingAddressForm, setMailingAddressFormData] = useState(
    getInitialMailingAddressState(formData.mailingAddress)
  );
  const [mailingAddressSearchValue, setMailingAddressSearchValue] = useState(() =>
    buildAddressSearchValue(formData.mailingAddress)
  );

  useEffect(() => {
    const updatedResidenceFormData = {
      ...sessionData.formData,
      residenceAddress: {
        ...sessionData.formData.residenceAddress,
        ...residenceAddressForm,
      },
    };
    updateMembershipForm(updatedResidenceFormData);
  }, [residenceAddressForm]);

  useEffect(() => {
    const updatedMailingAddressFormData = {
      ...sessionData.formData,
      mailingAddress: {
        ...sessionData.formData.mailingAddress,
        ...mailingAddressForm,
      },
    };
    updateMembershipForm(updatedMailingAddressFormData);
  }, [mailingAddressForm]);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }

    tag();
  }, [membershipInfo]);

  const showOutOfTerritoryError = () => {
    sendTag(UtagType.link, pageNameTag, {
      error: `${isGiftFlow ? pageNameTag : `membership:join:${pageNameTag}`}-out of territory`,
    });
    setServerValidationErrors({
      hasError: true,
      error: `Your zip code is not part of this club's coverage area.`,
    });
  };

  const navigateToNextPage = async (overrides?: { sessionId?: string; formData?: NewMembershipFormData }) => {
    loadingProvider.startLoading('Saving your membership information');
    await getNewBusinessCost(overrides?.formData, overrides?.sessionId);
    loadingProvider.endLoading();
    history.push(getNextPage(currentPage, sessionData.flowType));
  };

  // Re-initializes the session for the new club after a zip/club switch. If the new club no longer supports the
  // gender the member previously selected, it routes back to contact details to re-select and returns null so
  // callers stop the forward flow.
  const reinitializeForNewClub = async (): Promise<{ sessionId: string; formData: NewMembershipFormData } | null> => {
    const {
      sessionId: newSessionId,
      formData: newFormData,
      selectedGenderSupported,
    } = await reinitializeSessionForZipChange();
    if (!selectedGenderSupported) {
      loadingProvider.endLoading();
      history.push({ pathname: Pages.ContactDetailsPage, state: { genderReselectRequired: true } });
      return null;
    }
    return { sessionId: newSessionId, formData: newFormData };
  };

  const progressProps = getProgressProps(sessionData.flowType!, currentPage);

  const clearSelectedAddress = () => {
    setAddressSelected(false);
    if (isGettingResidenceAddress) {
      setResidenceAddressSearchValue(null);
      setResidenceAddressFormData((prev) => ({
        ...prev,
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        googleAddressId: '',
      }));
      return;
    }
    setMailingAddressSearchValue(null);
    setMailingAddressFormData((prev) => ({
      ...prev,
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      googleAddressId: '',
    }));
  };

  const submitForm = async (props?: {
    skipApiAddressValidation?: boolean;
    sessionIdOverride?: string;
    formDataOverride?: NewMembershipFormData;
    _isRetry?: boolean;
  }) => {
    try {
      loadingProvider.startLoading('Saving your membership information');
      setServerValidationErrors({ hasError: false, error: '' });

      if (!props?.sessionIdOverride && formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY) {
        await updateCache(sessionId || '', { requestedBillPlan: formData.billPlan });
      }

      const response = await submitMembershipForm(true, true, props?.sessionIdOverride, props?.formDataOverride);
      const isOutOfACETerritory = response?.isOutOfACETerritory ?? false;
      const isNotInZipClub = isOutOfACETerritory || response?.isClubCodeMismatch;

      const ersKey = `${formData.firstName}|${formData.lastName}|${formData.residenceAddress?.zipCode}`;
      const ersIdentityMatch = formData.hasPriorERSUsageKey === ersKey;
      const effectiveHasPriorERSUsage =
        (props?.formDataOverride?.hasPriorERSUsage ??
          response?.hasPriorERSUsage ??
          (ersIdentityMatch ? formData.hasPriorERSUsage : undefined)) === true;

      if (
        !props?.sessionIdOverride &&
        (response?.hasPriorERSUsage !== undefined || formData.hasPriorERSUsage !== undefined)
      ) {
        const baseFormData = props?.formDataOverride ?? sessionData.formData;
        updateMembershipForm({
          ...baseFormData,
          hasPriorERSUsage: effectiveHasPriorERSUsage,
          hasPriorERSUsageKey: ersKey,
        });
      }

      if (response?.isActiveMember) {
        sendTag(UtagType.link, pageNameTag, {
          error: `${isGiftFlow ? pageNameTag : `membership:join:${pageNameTag}`}-current active member (VALD)`,
        });
        if (isTMobileFlow) {
          history.push(Pages.MemberExists);
        } else if (isGrouponFlow) {
          history.push(Pages.PartnerMemberExists);
        } else {
          setServerValidationErrors({
            hasError: true,
            error: 'An active Membership is associated with your name and address.',
          });
        }
        loadingProvider.endLoading();
      } else if (isEmployeeFlow && (!response.isEmployeeClubValid || isOutOfACETerritory)) {
        setEmployeeInvalidClubModal(true);
        loadingProvider.endLoading();
      } else if (isGrouponFlow && (!response.isGrouponClubValid || isNotInZipClub)) {
        history.push(Pages.OfferNotAvailable);
      } else if (isMajorRocketFlow && isNotInZipClub) {
        showOutOfTerritoryError();
        loadingProvider.endLoading();
      } else if (isGiftFlow && isNotInZipClub) {
        history.push({
          pathname: Pages.GiftRecipientClubMismatchPage,
          state: { isOutOfACETerritory, zipCode: residenceAddressForm?.zipCode ?? '' },
        });
        loadingProvider.endLoading();
      } else if (isOutOfACETerritory) {
        setShowOotModal(true);
        loadingProvider.endLoading();
      } else if (response?.isClubCodeMismatch) {
        const mismatchPricing = response.clubMismatchPricing;
        if (mismatchPricing != null) {
          const isMonthly = formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY;
          const currentMemberPrice = isMonthly
            ? getCostByMembershipLevel(membershipInfo!, formData.membershipLevel!, 'monthly', sessionData)
            : getCostByMembershipLevel(membershipInfo!, formData.membershipLevel!, 'annual', sessionData);
          const billPlanChanged = mismatchPricing.billPlan !== formData.billPlan;
          const priceChanged = mismatchPricing.primaryMemberCost !== currentMemberPrice;

          if (billPlanChanged || priceChanged) {
            setClubMismatchPricing(mismatchPricing);
            setShowClubMismatchModal(true);
            loadingProvider.endLoading();
            return;
          }
        }
        if (!props?._isRetry) {
          const reinitialized = await reinitializeForNewClub();
          if (reinitialized == null) return;
          await submitForm({
            skipApiAddressValidation: true,
            sessionIdOverride: reinitialized.sessionId,
            formDataOverride: reinitialized.formData,
            _isRetry: true,
          });
        } else {
          loadingProvider.endLoading();
        }
      } else if (
        effectiveHasPriorERSUsage &&
        (props?.formDataOverride?.billPlan ?? formData.billPlan) === CurrentPayPlan.RECURRINGMONTHLY
      ) {
        setShowAnnualOnlyModal(true);
        loadingProvider.endLoading();
      } else if (hasMailingAddress && isGettingResidenceAddress) {
        sources.processRexEvent({
          action: 'post',
          sessionId: sessionId || '',
          flowId: sessionData?.flowType || '',
          screenId: 'findaddress/home',
          eventType: 'INPROGRESS',
          eventData: sessionData.formData,
        });
        history.push(Pages.MailingAddressPage);
      } else {
        // At this point the member has been validated but did not check the mailing address checkbox
        sources.processRexEvent({
          action: 'post',
          sessionId: props?.sessionIdOverride || sessionId || '',
          flowId: sessionData?.flowType || '',
          screenId: 'findaddress/home',
          eventType: 'INPROGRESS',
          // What event data do we need to pass here? If anything
          eventData: sessionData.formData,
        });
        navigateToNextPage({ sessionId: props?.sessionIdOverride, formData: props?.formDataOverride });
      }
    } catch (e) {
      const isBotDetectionResponse =
        // @ts-ignore
        e?.error?.response?.status === 403 && !!e.error?.response?.data?.pxEvent;
      if (isBotDetectionResponse) {
        loadingProvider.endLoading();
        window._pxOnCaptchaSuccess = function (isValid) {
          if (isValid) {
            submitForm(props);
          }
        };
      } else {
        history.push(Pages.SorryPage);
      }
    }
  };

  const clearResidenceAddressForm = () => {
    updateMembershipForm({
      ...formData,
      residenceAddress: getInitialResidenceAddressState({} as Address),
    });
  };

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  const unitTypeList = [
    { name: 'N/A', value: 'N/A' },
    { name: 'Apt', value: 'Apt' },
    { name: 'Unit', value: 'Unit' },
    { name: 'Suite', value: 'Suite' },
    { name: 'Floor', value: 'Floor' },
    { name: 'Room', value: 'Room' },
    { name: 'Building', value: 'Building' },
    { name: 'Other', value: 'Other' },
  ];

  const annualDiscountedPrice = getCostByMembershipLevel(
    membershipInfo,
    formData.membershipLevel || MembershipLevel.CLASSIC,
    'annual',
    sessionData
  );
  const monthlyAllowed = membershipInfo.allowedBillPlans.includes(CurrentPayPlan.RECURRINGMONTHLY);
  const monthlyPrice = monthlyAllowed
    ? getCostByMembershipLevel(
        membershipInfo,
        formData.membershipLevel || MembershipLevel.CLASSIC,
        'monthly',
        sessionData
      )
    : 0;

  const isCurrentBillPlanMonthly = formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY;
  const currentPrice = isCurrentBillPlanMonthly ? monthlyPrice : annualDiscountedPrice;
  const currentPriceLabel = isCurrentBillPlanMonthly ? 'month' : 'year';
  const newBillPlanIsMonthly = clubMismatchPricing?.billPlan === CurrentPayPlan.RECURRINGMONTHLY;
  const newPriceLabel = newBillPlanIsMonthly ? 'month' : 'year';
  const hasBillPlanChange = clubMismatchPricing != null && clubMismatchPricing.billPlan !== formData.billPlan;
  const hasPriceChange = clubMismatchPricing != null && clubMismatchPricing.primaryMemberCost !== currentPrice;

  return (
    <>
      <AppProgressHeader {...progressProps} />
      {loadingInfo.isLoading ? (
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={2} currentStep={loadingInfo.loadingDetails.length} />
      ) : (
        <>
          <EmployeeInvalidClubModal
            open={showEmployeeInvalidClubModal}
            onClose={() => {
              setEmployeeInvalidClubModal(false);
            }}
          />
          <Box className="auto-complete-address-header-container">
            <Typography variant="h5" className="auto-complete-address-header-title">
              {getAddressPageTitle(isGiftFlow, isGettingResidenceAddress)}
            </Typography>
            {serverValidationErrors?.hasError ? (
              <Alert
                children={
                  <Typography className="validation-error-alert" variant="b3Regular">
                    {serverValidationErrors?.error}
                  </Typography>
                }
                severity="error"
                displayCloseButton={false}
              />
            ) : isGiftFlow ? (
              <></>
            ) : (
              <Typography variant="b1Regular" className="auto-complete-address-header-text">
                We need your address to validate your Auto Club membership.
              </Typography>
            )}
          </Box>

          <Form sx={{ marginTop: 0 }}>
            <Box className="address-form qm-mask">
              <AddressSearch
                action={{
                  label: 'I do not see my address',
                  onClick: () => {
                    //clear unit info to avoid duplications after manual address entry
                    clearResidenceAddressForm();
                    history.push(isGettingResidenceAddress ? Pages.ResidenceAddressPage : Pages.MailingAddressPage);
                  },
                }}
                sx={{ marginTop: '1rem' }}
                value={isGettingResidenceAddress ? residenceAddressSearchValue : mailingAddressSearchValue}
                helperText=""
                placeholder="Type in"
                label="Address"
                className={isGettingResidenceAddress ? 'residence-address-search' : 'mailing-address-search'}
                id="address"
                fullWidth
                required
                data-qm-block="true"
                validator={addressValidator}
                onChange={(v) => {
                  setAddressSelected(true);
                  if (isGettingResidenceAddress && v != null) {
                    setResidenceAddressSearchValue((v as any) ?? null);
                    setResidenceAddressFormData((prev) => ({
                      ...prev,
                      googleAddressId: (v as any)?.id ?? '',
                      streetAddress: (v as any)?.longParts?.street ?? '',
                      city: (v as any)?.longParts?.city ?? '',
                      state: (v as any)?.shortParts?.state ?? '',
                      zipCode: (v as any)?.longParts?.zip ?? '',
                    }));
                    return;
                  }
                  if (v !== null) {
                    setMailingAddressSearchValue((v as any) ?? null);
                    setMailingAddressFormData((prev) => ({
                      ...prev,
                      googleAddressId: (v as any)?.id ?? '',
                      streetAddress: (v as any)?.longParts?.street ?? '',
                      city: (v as any)?.longParts?.city ?? '',
                      state: (v as any)?.shortParts?.state ?? '',
                      zipCode: (v as any)?.longParts?.zip ?? '',
                    }));
                  }
                }}
                onClearButtonClick={clearSelectedAddress}
              />
              <Grid container marginTop="1rem">
                <Grid size={6}>
                  <Select
                    label="Unit type"
                    //the dropdown will be full screen when number of items reaches 6(default threshold), so we set up a larger number here to avoid full screen dropdown
                    fullScreenThreshold={10}
                    fullWidth
                    name="unitType"
                    placeholder="Select"
                    displayEmpty
                    id="unitTypeDropdown"
                    helperText="Only if applicable"
                    value={unitTypeList.find((u) => {
                      if (isGettingResidenceAddress) {
                        return u.value === (residenceAddressForm.unitType as string);
                      }
                      return u.value === (mailingAddressForm.unitType as string);
                    })}
                    getDisplayText={(v: any) => {
                      return v.name;
                    }}
                    onChange={(v: any) => {
                      const x = v.target.value;
                      if (isGettingResidenceAddress) {
                        if (x.value === 'N/A') {
                          setResidenceAddressFormData((prev) => ({
                            ...prev,
                            unitNumber: '',
                          }));
                        }
                        setResidenceAddressFormData((prev) => ({
                          ...prev,
                          unitType: x.value,
                        }));
                        return;
                      }
                      setMailingAddressFormData((prev) => ({
                        ...prev,
                        unitType: x.value,
                      }));
                      if (x.value === 'N/A') {
                        setMailingAddressFormData((prev) => ({
                          ...prev,
                          unitNumber: '',
                        }));
                      }
                    }}
                  >
                    {unitTypeList.map((unitType, i) => (
                      <MenuItem key={unitType.name} value={unitType as any} id={unitType.value}>
                        {unitType.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid size={6}>
                  <TextField
                    value={
                      isGettingResidenceAddress
                        ? (residenceAddressForm.unitNumber ?? '')
                        : (mailingAddressForm.unitNumber ?? '')
                    }
                    label="Unit number"
                    disabled={
                      isGettingResidenceAddress
                        ? residenceAddressForm.unitType === 'N/A'
                        : mailingAddressForm.unitType === 'N/A'
                    }
                    id="unit-number"
                    name="unitNumber"
                    className="address-unit-number"
                    data-qm-block="true"
                    placeholder="e.g. 4B, 300, A"
                    fullWidth
                    onChange={(v) => {
                      const unitNumber = v.currentTarget.value;
                      if (isGettingResidenceAddress) {
                        setResidenceAddressFormData((prev) => ({
                          ...prev,
                          unitNumber,
                        }));
                        return;
                      }
                      setMailingAddressFormData((prev) => ({
                        ...prev,
                        unitNumber,
                      }));
                    }}
                    onClearButtonClick={() => {
                      isGettingResidenceAddress
                        ? setResidenceAddressFormData((prev) => ({ ...prev, unitNumber: '' }))
                        : setMailingAddressFormData((prev) => ({ ...prev, unitNumber: '' }));
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiFormLabel-asterisk': {
                        display: 'none',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {isGettingResidenceAddress && (
              <Box
                sx={{ marginTop: '1rem', '& .MuiFormControlLabel-root': { width: '100%', padding: '0 !important' } }}
              >
                <Checkbox
                  key={formKey}
                  name="check-different-mailing"
                  value={hasMailingAddress}
                  label={differentAddressText}
                  id="check-different-mailing"
                  variant="text"
                  checked={hasMailingAddress}
                  onChange={(e) => {
                    setHasMailingAddress(e.checked);
                    setFormKey(new Date().toISOString());
                    if (!e.checked) {
                      setMailingAddressFormData(() => ({
                        streetAddress: '',
                        city: '',
                        state: '',
                        zipCode: '',
                      }));
                    }
                  }}
                  sx={{
                    '& .MuiCheckbox-FormControlLabel:focus-within': {
                      outline: 'transparent !important',
                      boxShadow: 'none !important',
                    },
                    '& .MuiCheckbox-root.Mui-focusVisible': {
                      outline: 'transparent !important',
                      boxShadow: 'none !important',
                    },
                  }}
                  fullWidth
                  className="check-items"
                ></Checkbox>
              </Box>
            )}

            <Box className="auto-complete-address-form-btn">
              <Button
                label="Next"
                fullWidth
                onClick={() => {
                  submitForm();
                }}
                sx={{ marginTop: '1.5rem' }}
                type="submit"
                variant="contained"
                size="medium"
                id="nextButton"
              ></Button>
            </Box>

            <ConfirmZipCodeModal
              isOpen={showOotModal}
              closeModal={() => {
                showOutOfTerritoryError();
                setShowOotModal(false);
              }}
              confirm={async () => {
                setShowOotModal(false);
                const zipCode = sessionData.formData.residenceAddress.zipCode;
                window.location.assign(`https://zipgate.aaa.com/?ZipCodeEntry=${zipCode}`);
              }}
            />

            <MembershipUpdateModal
              modalTitle="Annual membership required"
              modalSubtitle="Monthly membership isn't available. To continue, you'll need to choose an annual plan."
              isOpen={showAnnualOnlyModal}
              modalContentTitle={<>Changes to your membership:</>}
              modalContentItems={[
                { itemTitle: 'Billing', beforeChange: 'Monthly', afterChange: 'Annual' },
                {
                  itemTitle: 'Price',
                  beforeChange: <>{numberHelper.formatMoney(monthlyPrice)}/month</>,
                  afterChange: <>{numberHelper.formatMoney(annualDiscountedPrice)}/year</>,
                },
              ]}
              closeModal={() => {
                setShowAnnualOnlyModal(false);
              }}
              continueButtonLabel="Continue with annual"
              onContinue={async () => {
                setShowAnnualOnlyModal(false);
                const annualBillPlan = allowOneTime
                  ? CurrentPayPlan.ONETIME
                  : CurrentPayPlan.RECURRINGCREDITORDEBITCARD;
                const ersKey = `${formData.firstName}|${formData.lastName}|${formData.residenceAddress?.zipCode}`;
                const updatedFormData = {
                  ...sessionData.formData,
                  hasPriorERSUsage: true,
                  hasPriorERSUsageKey: ersKey,
                  billPlan: annualBillPlan,
                };
                updateMembershipSession({ ...sessionData, formData: updatedFormData, priceCart: null });
                loadingProvider.startLoading('Saving your membership information');
                await getNewBusinessCost(updatedFormData);
                loadingProvider.endLoading();
                history.push(getNextPage(currentPage, sessionData.flowType));
              }}
            />

            <MembershipUpdateModal
              modalTitle="Review membership updates"
              modalSubtitle={`Based on ZIP code ${residenceAddressForm?.zipCode}, some membership details will change. Before you continue, review the changes below.`}
              isOpen={showClubMismatchModal}
              modalContentTitle={<>Changes to your membership:</>}
              editButtonLabel="Edit address"
              onEdit={() => {
                setShowClubMismatchModal(false);
              }}
              modalContentItems={[
                ...(hasBillPlanChange
                  ? [
                      {
                        itemTitle: 'Billing',
                        beforeChange: isCurrentBillPlanMonthly ? 'Monthly' : 'Annual',
                        afterChange: newBillPlanIsMonthly ? 'Monthly' : 'Annual',
                      },
                    ]
                  : []),
                ...(hasPriceChange
                  ? [
                      {
                        itemTitle: 'Price',
                        beforeChange: (
                          <>
                            {numberHelper.formatMoney(currentPrice)}/{currentPriceLabel}
                          </>
                        ),
                        afterChange: (
                          <>
                            {numberHelper.formatMoney(clubMismatchPricing?.primaryMemberCost ?? 0)}/{newPriceLabel}
                          </>
                        ),
                      },
                    ]
                  : []),
              ]}
              closeModal={() => {
                setShowClubMismatchModal(false);
                setClubMismatchPricing(null);
              }}
              continueButtonLabel="Continue"
              onContinue={async () => {
                setShowClubMismatchModal(false);
                setClubMismatchPricing(null);
                const reinitialized = await reinitializeForNewClub();
                if (reinitialized == null) return;
                await submitForm({
                  skipApiAddressValidation: true,
                  sessionIdOverride: reinitialized.sessionId,
                  formDataOverride: reinitialized.formData,
                });
              }}
            />
          </Form>
        </>
      )}
    </>
  );
};
